import { LinearProgress, Typography } from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Card, Flex, FlexColumn, Link } from '@elementary/common-ui/components';
import { numberWithCommas } from '@elementary/common-ui/utils/common';

const SUPPORT_MAILTO = 'mailto:support@elementary-data.com';

type Props = {
  title: string;
  subtitle: string;
  creditsUsed: number;
  creditLimit: number | null;
  resetsText: string;
  limitReachedText: string;
};

const LimitReachedChip = ({ text }: { text: string }) => (
  <Flex
    sx={{
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderRadius: 1.5,
      backgroundColor: 'common.orange10',
    }}
  >
    <ErrorOutlineRoundedIcon sx={{ fontSize: 18, color: 'common.orange100' }} />
    <Typography sx={{ fontSize: 14 }}>
      {text} <Link href={SUPPORT_MAILTO}>Contact us.</Link>
    </Typography>
  </Flex>
);

const ResetsAt = ({ text }: { text: string }) => (
  <Typography sx={{ fontSize: 13, color: 'common.gray2' }}>{text}</Typography>
);

const Heading = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <FlexColumn sx={{ gap: 0.25 }}>
    <Typography sx={{ fontWeight: 'bold', fontSize: 16 }}>{title}</Typography>
    <Typography sx={{ fontSize: 13, color: 'common.gray2' }}>
      {subtitle}
    </Typography>
  </FlexColumn>
);

const CreditsCard = ({
  title,
  subtitle,
  creditsUsed,
  creditLimit,
  resetsText,
  limitReachedText,
}: Props) => {
  const used = Math.round(creditsUsed);

  if (creditLimit === null) {
    return (
      <Card>
        <FlexColumn sx={{ gap: 1.5, width: '100%' }}>
          <Heading title={title} subtitle={subtitle} />
          <Typography sx={{ fontWeight: 'bold', fontSize: 28 }}>
            {numberWithCommas(used)}
          </Typography>
          <ResetsAt text={resetsText} />
        </FlexColumn>
      </Card>
    );
  }

  const pct =
    creditLimit > 0
      ? Math.min(100, Math.round((used / creditLimit) * 100))
      : 100;
  const isLimitReached = used >= creditLimit;

  return (
    <Card>
      <FlexColumn sx={{ gap: 1.5, width: '100%' }}>
        <Heading title={title} subtitle={subtitle} />
        <Flex sx={{ alignItems: 'center', gap: 2 }}>
          <Flex sx={{ alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: 28 }}>
              {pct}%
            </Typography>
            <Typography sx={{ fontSize: 28, color: 'common.gray2' }}>
              {numberWithCommas(used)} / {numberWithCommas(creditLimit)}
            </Typography>
          </Flex>
          {isLimitReached && <LimitReachedChip text={limitReachedText} />}
        </Flex>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <ResetsAt text={resetsText} />
      </FlexColumn>
    </Card>
  );
};

export default CreditsCard;
